import {memo, lazy, Suspense, useRef} from 'react'
import {createPortal} from 'react-dom'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {ssrSafeDocument} from '@github-ui/ssr-utils'
import {XIcon} from '@primer/octicons-react'
import {IconButton, Spinner} from '@primer/react'
import {Dialog} from '@primer/react/experimental'
import {DOCKED_SIDE_PANEL_CONTENT_ID} from './DockedSidePanel'
import {Panel} from './Panel'
import type {Repository} from '@github-ui/current-repository'
import {
  DockedPanel,
  useDockedPanelActions,
  useDockedPanelFocusRefs,
  useIsDockedPanelCompact,
  useIsDockedPanelOpen,
} from '../../contexts/DockedPanelContext'

const PullRequestsSidePanel = lazy(async () => {
  const m = await import('@github-ui/agent-sessions/components/task-view/PullRequestsSidePanel')
  return {default: m.PullRequestsSidePanel}
})

function AgentSessionsPanelBody({repository, pullRequestNumber}: {repository: Repository; pullRequestNumber: number}) {
  return (
    <Suspense
      fallback={
        <div className="d-flex flex-justify-center flex-items-center tmp-p-4">
          <Spinner size="medium" />
        </div>
      }
    >
      <PullRequestsSidePanel repository={repository} pullRequestNumber={pullRequestNumber} />
    </Suspense>
  )
}

interface AgentSessionsSidePanelProps {
  isOpen: boolean
  repository: Repository
  pullRequestNumber: number
}

export const AgentSessionsSidePanel = memo(function AgentSessionsSidePanel({
  isOpen,
  repository,
  pullRequestNumber,
}: AgentSessionsSidePanelProps) {
  const isPrsDockedSidePanelEnabled = isFeatureEnabled('prs_docked_side_panel')
  const isDockedPanelOpen = useIsDockedPanelOpen(DockedPanel.AgentSessions)
  const isCompact = useIsDockedPanelCompact()
  const {closePanel} = useDockedPanelActions()
  const {returnFocusRef} = useDockedPanelFocusRefs()
  const closeButtonRef = useRef<HTMLButtonElement>(null)

  const sidePanelContent = isDockedPanelOpen ? ssrSafeDocument?.getElementById(DOCKED_SIDE_PANEL_CONTENT_ID) : null

  if (!isOpen) return null

  if (isPrsDockedSidePanelEnabled && sidePanelContent && !isCompact) {
    return createPortal(
      <Panel panelName={DockedPanel.AgentSessions}>
        <Panel.Body>
          <AgentSessionsPanelBody repository={repository} pullRequestNumber={pullRequestNumber} />
        </Panel.Body>
      </Panel>,
      sidePanelContent,
    )
  }

  return (
    <Dialog
      initialFocusRef={closeButtonRef}
      position={{narrow: 'fullscreen', regular: 'right', wide: 'right'}}
      returnFocusRef={returnFocusRef}
      onClose={closePanel}
      renderHeader={({dialogLabelId}) => (
        <Dialog.Header>
          <div className="d-flex flex-row flex-justify-between flex-items-center width-full pl-2">
            <Dialog.Title id={dialogLabelId}>{DockedPanel.AgentSessions}</Dialog.Title>
            <IconButton
              ref={closeButtonRef}
              aria-label="Close agent sessions panel"
              icon={XIcon}
              variant="invisible"
              onClick={closePanel}
            />
          </div>
        </Dialog.Header>
      )}
    >
      <AgentSessionsPanelBody repository={repository} pullRequestNumber={pullRequestNumber} />
    </Dialog>
  )
})
